/**
 * Public marketing copy and the tier comparison.
 *
 * The tier data mirrors the live `subscription_plans` rows: prices in cents
 * (monthly and annual), the ENFORCED limits (seats, teams, seasons of history,
 * games per season, custom stat fields and `limits.storage_bytes`) and the
 * feature bundle. It is static on purpose: the landing page is public and
 * pre-auth, so it must render with zero API calls. When the billing service
 * ships `GET /billing/v1/plans`, this becomes its fallback.
 *
 * Copy rule: every claim here is backed by the product as it ships. No
 * invented numbers, testimonials or customer logos.
 */
import { ROUTES, TRIAL } from './constants';

export const HERO = {
  kicker: 'Live football stats for coaching staffs and their fans',
  headlineTop: 'Chart every snap.',
  headlineBottom: 'Call the next one.',
  subheadline:
    'Your stat crew logs each play on a tablet as it happens. Coordinators see tendencies, success rates and the drive before the next snap, and fans follow the game from the stands.',
  primaryCta: `Start ${TRIAL.days}-Day Free Trial`,
  primaryCtaHref: ROUTES.signup,
  secondaryCta: 'Walk the field',
  trialCallout: `${TRIAL.days}-day ${TRIAL.planDisplayName} trial. No card required. Automatic downgrade to ${TRIAL.downgradePlanDisplayName} on day ${TRIAL.days}.`,
} as const;

/**
 * One stop on the landing page's drive down the field. `down` and `spot` are
 * the broadcast-style down-and-distance tag that heads the section.
 */
export interface DriveStop {
  id: string;
  down: string;
  spot: string;
  /** Ball position in yards from our own goal line (0–100). */
  yard: number;
  /** Line to gain, in the same yards. */
  lineToGain: number;
  kicker: string;
  title: string;
  body: string;
  points: readonly string[];
}

export const DRIVE: readonly DriveStop[] = [
  {
    id: 'play-entry',
    down: '1st & 10',
    spot: 'Own 25',
    yard: 25,
    lineToGain: 35,
    kicker: 'Play Entry',
    title: 'Log the snap in a few taps',
    body: 'One operator or several work the same live game. Each taps their part of the play on a touch board built for the press box and the sideline, and the pieces come together as one play.',
    points: [
      'Keeps working when the stadium Wi-Fi drops, and syncs when it comes back.',
      'When two operators enter the same play differently, both versions are flagged so someone can pick the right one.',
      'Each team arranges its own entry boards.',
    ],
  },
  {
    id: 'game-summary',
    down: '2nd & 2',
    spot: 'Own 33',
    yard: 33,
    lineToGain: 35,
    kicker: 'Game Summary and Live Video',
    title: 'The scoreboard keeps itself',
    body: 'Every logged play updates the score, the quarter line, the drive log and the offense, defense and special teams stat grids. Put the game video next to a live stat rail so the staff watches and reads in one place.',
    points: [
      'Starter and up get live push updates. Free refreshes live screens every 5 seconds.',
      'Choose the video provider per team or per event.',
    ],
  },
  {
    id: 'highlight',
    down: '1st & 10',
    spot: 'Own 40',
    yard: 40,
    lineToGain: 50,
    kicker: 'Coaches Highlight',
    title: 'The numbers that decide the next call',
    body: 'The offensive coordinator opens an offense view and the defensive coordinator a defense view. Breakdowns, success rates, matchups and trends are computed from the plays as they are logged, and every card follows the same filters.',
    points: [
      'Tap a bar to filter the whole board to that look.',
      'Each coordinator arranges and saves their own board.',
    ],
  },
  {
    id: 'stats',
    down: '2nd & 2',
    spot: 'Own 48',
    yard: 48,
    lineToGain: 50,
    kicker: 'Stat Tracker and Play Archive',
    title: 'Game and season numbers, always current',
    body: 'Stat Tracker rolls every play into game and season totals. Play Archive keeps the full play list, sortable and filterable, with a column picker and the edit history of every play.',
    points: [
      'Export any table to CSV or XLSX.',
      'Add custom stats for whatever your staff tracks.',
    ],
  },
  {
    id: 'hudl',
    down: '1st & 10',
    spot: 'Opp 45',
    yard: 55,
    lineToGain: 65,
    kicker: 'Hudl and Playbook',
    title: 'Film and the playbook, tied to the data',
    body: 'Import a Hudl playlist from XLSX or CSV, or export a game as a Hudl-compatible spreadsheet or a custom layout saved as a template. The playbook keeps your concepts by category, stock and team options side by side, each with its play count.',
    points: [
      'Games, practices and scrimmages all live in Events.',
      'Film review mode when the game is over.',
    ],
  },
  {
    id: 'fans',
    down: '2nd & 2',
    spot: 'Opp 37',
    yard: 63,
    lineToGain: 65,
    kicker: 'Fans',
    title: 'Parents and fans follow from the stands',
    body: 'Fans sign up once, follow the teams they care about and watch the live video and the game summary on their phones. Each team decides how fans get access.',
    points: ['Fans never need a team code.', 'Built for the phone in the bleachers.'],
  },
];

/** Down-and-distance tags for the sections that follow the drive stops. */
export const DRIVE_FINISH = {
  program: { down: '1st & 10', spot: 'Opp 29', yard: 71, lineToGain: 81 },
  pricing: { down: '1st & 10', spot: 'Opp 15', yard: 85, lineToGain: 95 },
  touchdown: { down: 'Touchdown', spot: 'End zone', yard: 100, lineToGain: 100 },
} as const;

/** The rest of the program, as a plain spec list (no icon grid). */
export const PROGRAM_SPECS: readonly { term: string; detail: string }[] = [
  {
    term: 'Team users',
    detail:
      'Invite staff by email as team admin, offensive or defensive coordinator, coach, or primary or general stat operator.',
  },
  { term: 'Roster', detail: 'Players grouped into cohorts such as varsity, JV and freshman.' },
  {
    term: 'Events',
    detail: 'Games, practices and scrimmages, with a film review mode after the game.',
  },
  {
    term: 'Custom stats',
    detail: 'Add the fields your staff tracks and use them in entry, filters and exports.',
  },
  {
    term: 'Single sign-on',
    detail: 'SAML sign-in through your district or college identity provider on Elite.',
  },
  {
    term: 'Team settings',
    detail: 'Video provider per team or event, support routing and your own success measures.',
  },
];

export interface Tier {
  name: string;
  displayName: string;
  badge: string | null;
  isFeatured: boolean;
  priceCents: number;
  /** Price when billed yearly, in cents. */
  annualPriceCents: number;
  seats: number;
  summary: string;
  ctaLabel: string;
  ctaHref: string;
  features: readonly { label: string; isIncluded: boolean }[];
}

export const TIERS: readonly Tier[] = [
  {
    name: 'free',
    displayName: 'Free',
    badge: 'Always Free',
    isFeatured: false,
    priceCents: 0,
    annualPriceCents: 0,
    seats: 2,
    summary: 'Five games a season for one team. Enough to chart a scrimmage and see the value.',
    ctaLabel: 'Get Started',
    ctaHref: ROUTES.signup,
    features: [
      { label: '2 seats, 1 team', isIncluded: true },
      { label: '5 games per season, 1 season of history', isIncluded: true },
      { label: 'Play Entry, Game Summary, Play Archive', isIncluded: true },
      { label: 'Live screens refresh every 5 seconds', isIncluded: true },
      { label: '100 MB storage', isIncluded: true },
      { label: 'Stat Tracker', isIncluded: false },
      { label: 'Coaches Highlight', isIncluded: false },
      { label: 'Hudl import / export', isIncluded: false },
    ],
  },
  {
    name: 'starter',
    displayName: 'Starter',
    badge: 'Popular',
    isFeatured: false,
    priceCents: 4900,
    annualPriceCents: 49900,
    seats: 15,
    summary: 'Unlimited games and live push updates for a single program.',
    ctaLabel: 'Start Trial',
    ctaHref: ROUTES.signup,
    features: [
      { label: '15 seats, 1 team', isIncluded: true },
      { label: 'Unlimited games, 1 season of history', isIncluded: true },
      { label: 'Live push updates', isIncluded: true },
      { label: 'Stat Tracker', isIncluded: true },
      { label: 'Playbook (limited)', isIncluded: true },
      { label: '10 custom stat fields', isIncluded: true },
      { label: '5 GB storage', isIncluded: true },
      { label: 'Coaches Highlight', isIncluded: false },
      { label: 'Hudl import / export', isIncluded: false },
    ],
  },
  {
    name: 'varsity',
    displayName: 'Varsity',
    badge: 'Trial Offer',
    isFeatured: true,
    priceCents: 14900,
    annualPriceCents: 149900,
    seats: 50,
    summary: `Everything a varsity staff needs on game day. Included free for ${TRIAL.days} days.`,
    ctaLabel: `Start ${TRIAL.days}-Day Trial`,
    ctaHref: ROUTES.signup,
    features: [
      { label: '50 seats, 1 team, 3 seasons of history', isIncluded: true },
      { label: 'Everything in Starter', isIncluded: true },
      { label: 'Coaches Highlight (offense and defense)', isIncluded: true },
      { label: 'Hudl import / export', isIncluded: true },
      { label: 'Live video embed', isIncluded: true },
      { label: 'Unlimited playbook', isIncluded: true },
      { label: '5 GB storage', isIncluded: true },
      { label: 'SAML / SCIM, API access', isIncluded: false },
    ],
  },
  {
    name: 'elite',
    displayName: 'Elite',
    badge: 'Enterprise',
    isFeatured: false,
    priceCents: 29900,
    annualPriceCents: 299900,
    seats: 999,
    summary: 'Districts and colleges running several teams, with SSO, provisioning and API access.',
    ctaLabel: 'Contact Sales',
    ctaHref: 'mailto:',
    features: [
      { label: 'Unlimited seats, up to 5 teams', isIncluded: true },
      { label: 'Everything in Varsity', isIncluded: true },
      { label: 'SAML 2.0 SSO and SCIM provisioning', isIncluded: true },
      { label: 'Custom roles', isIncluded: true },
      { label: 'API access', isIncluded: true },
      { label: 'Unlimited history and custom stat fields', isIncluded: true },
      { label: '25 GB storage', isIncluded: true },
    ],
  },
];

export type BillingPeriod = 'monthly' | 'annual';

/** `$0`, `$49`, `$1,499` — whole dollars, US grouping. */
export function formatMonthlyPrice(priceCents: number): string {
  return priceCents === 0 ? '$0' : `$${Math.round(priceCents / 100).toLocaleString('en-US')}`;
}

/** Whole dollars saved per year by paying annually (0 for Free). */
export function annualSavingsDollars(tier: Tier): number {
  return Math.max(0, Math.round((tier.priceCents * 12 - tier.annualPriceCents) / 100));
}

/**
 * Sales/contact address from build-time config (`VITE_CONTACT_SALES_EMAIL`).
 *
 * `null` when the deployment has not configured one — callers then fall back
 * to the signup flow / hide the contact link. Never returns a fabricated
 * default: a "Contact sales" button that composes an undeliverable email is
 * strictly worse than no button.
 */
export function contactSalesEmail(): string | null {
  const configured = import.meta.env.VITE_CONTACT_SALES_EMAIL;
  return typeof configured === 'string' && configured.trim() !== '' && configured.includes('@')
    ? configured.trim()
    : null;
}
